/**
 * HOOK: useAuth
 *
 * Gestiona autenticación Firebase + sesión local + validación admin.
 * Single source of truth for auth state — used by App.jsx.
 */

import { useState, useEffect } from 'react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { getCurrentUserRole } from '../utils/authHelper'

export function useAuth() {
  const [userRef, setUserRef] = useState(() => localStorage.getItem('userRef') || '')
  const [userToken, setUserToken] = useState(() => localStorage.getItem('userToken') || null)
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(() => !!localStorage.getItem('userToken'))
  const [authReady, setAuthReady] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [authUid, setAuthUid] = useState('')
  const [userDisplayName, setUserDisplayName] = useState(() => localStorage.getItem('userDisplayName') || '')
  const [forcePasswordSetup, setForcePasswordSetup] = useState(false)
  const [forcePasswordChange, setForcePasswordChange] = useState(false)

  /**
   * Escuchar cambios de autenticación Firebase
   */
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), async (user) => {
      if (!user) {
        setAuthUid('')
        setIsUserAuthenticated(false)
        setUserToken(null)
        setUserRef('')
        setUserDisplayName('')
        setForcePasswordSetup(false)
        setForcePasswordChange(false)
        localStorage.removeItem('userToken')
        localStorage.removeItem('userRef')
        localStorage.removeItem('userDisplayName')
        setAuthReady(true)
        return
      }

      setAuthUid(user.uid || '')
      const label = String(user.displayName || user.email || '').trim()
      setUserDisplayName(label)
      if (label) localStorage.setItem('userDisplayName', label)

      try {
        const idToken = await user.getIdToken(true)
        setUserToken(idToken)
        setIsUserAuthenticated(true)
        localStorage.setItem('userToken', idToken)

        const response = await fetch('/api/user/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken }),
        })
        const data = await response.json()
        const resolvedUserRef = data?.userRef || data?.uid || user.uid || user.email || ''
        setForcePasswordSetup(Boolean(data?.user?.forcePasswordSetup))
        setForcePasswordChange(Boolean(data?.user?.forcePasswordChange))
        if (resolvedUserRef) {
          setUserRef(resolvedUserRef)
          localStorage.setItem('userRef', resolvedUserRef)
        }
      } catch {
        setIsUserAuthenticated(false)
        setUserToken(null)
        setUserRef('')
        localStorage.removeItem('userToken')
        localStorage.removeItem('userRef')
      } finally {
        setAuthReady(true)
      }
    })
    return () => unsub()
  }, [])

  /**
   * Re-validar rol admin cada vez que cambia userRef o isUserAuthenticated
   */
  useEffect(() => {
    let active = true
    const run = async () => {
      if (!isUserAuthenticated) return
      const authUser = getAuth().currentUser
      const needle = userRef || authUser?.email || authUser?.uid || ''
      if (!needle) return
      const role = await getCurrentUserRole(needle)
      if (active) setIsAdmin(Boolean(role.isAdmin))
    }
    run().catch(() => setIsAdmin(false))
    return () => { active = false }
  }, [userRef, isUserAuthenticated])

  /**
   * Manejar login exitoso desde el componente Landing
   * CUIT/UserRef no se persiste — privacy by design
   */
  const onLoginSuccess = (valueUserRef, token) => {
    setUserRef(valueUserRef)
    setUserToken(token)
    setIsUserAuthenticated(true)
    localStorage.setItem('userRef', valueUserRef)
    localStorage.setItem('userToken', token)
  }

  /**
   * Manejar logout completo — limpia estado y localStorage
   */
  const onLogout = () => {
    setUserRef('')
    setUserToken(null)
    setIsUserAuthenticated(false)
    localStorage.removeItem('userRef')
    localStorage.removeItem('userToken')
    localStorage.removeItem('userDisplayName')
    setUserDisplayName('')
    setForcePasswordSetup(false)
    setForcePasswordChange(false)
  }

  return {
    userRef,
    setUserRef,
    userToken,
    setUserToken,
    isUserAuthenticated,
    setIsUserAuthenticated,
    authReady,
    isAdmin,
    setIsAdmin,
    authUid,
    setAuthUid,
    userDisplayName,
    setUserDisplayName,
    forcePasswordSetup,
    setForcePasswordSetup,
    forcePasswordChange,
    setForcePasswordChange,
    onLoginSuccess,
    onLogout,
  }
}
